// ============================================================
// frontend/src/lib/auth.tsx
// ============================================================
// Auth context. Provides the current therapist to the whole app
// via `useAuth()`.
//
// On mount, calls /api/auth/me to check for an existing session.
// While checking, sets `loading = true` so consumers can show
// a loading spinner instead of flashing the login screen.
// ============================================================

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { api } from './api';
import type { Therapist, AuthResponse, RegisterResponse } from '@consilium/shared';

interface AuthContextValue {
  therapist: Therapist | null;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<Therapist>;
  // Register no longer logs in — returns the pending-approval payload so the
  // UI can show the right message.
  register: (email: string, password: string, name: string) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  // Re-fetch /auth/me. Used after the forced password change so
  // mustChangePassword flips to false and the app unlocks.
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [therapist, setTherapist] = useState<Therapist | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on first mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { therapist } = await api.get<AuthResponse>('/auth/me');
        if (!cancelled) setTherapist(therapist);
      } catch {
        // 401 = not logged in. Expected, not an error.
        if (!cancelled) setTherapist(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Listen for the global 'auth:expired' event from api.ts.
  // When any request returns 401, we clear the therapist → login screen renders.
  useEffect(() => {
    const onExpired = () => setTherapist(null);
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const login = useCallback(async (email: string, password: string, rememberMe: boolean = true) => {
    const { therapist } = await api.post<AuthResponse>('/auth/login', { email, password, rememberMe });
    setTherapist(therapist);
    return therapist;
  }, []);

  const register = useCallback(async (email: string, password: string, name: string) => {
    // Backend returns { status: 'pending', message } — no session is created.
    // The user must wait for admin approval before they can log in.
    const result = await api.post<RegisterResponse>('/auth/register', { email, password, name });
    return result;
  }, []);

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    setTherapist(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { therapist } = await api.get<AuthResponse>('/auth/me');
      setTherapist(therapist);
    } catch {
      setTherapist(null);
    }
  }, []);

  return (
    <AuthContext.Provider value={{ therapist, loading, login, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
